import { Injectable } from '@angular/core';

import { SvbListFilter } from '../models/svb-list-filters.model';
import { BrokerUsersFilter } from '../pages/broker-users/models/broker-users-filter.model';
import { BrokersFilter } from '../pages/brokers/models/brokers-filter.class';
import { CDAFilter } from '../pages/cda/models/cda-filter.class';
import { CommissionIssuersFilter } from '../pages/commission-issuers/models/commission-issuers-filter.class';
import { CreditCardsFilter } from '../pages/credit-cards/models/credit-cards-filter.model';
import { DealPromoCodeFilter } from '../pages/deal-discounts/models/discounts-filter.model';
import { DealStatusEnum } from '../pages/deals/models/deal.status.enum';
import { DealsFilter } from '../pages/deals/models/deals-filter.model';
import { DiscountsFilter } from '../pages/discounts/models/discounts-filter.model';
import { ExternalDealCommissionFilter } from '../pages/external-deals/models/external-deal-commission-filter.model';
import { FacilitiesFilter } from '../pages/facilities/models/facilities-filter.model';
import { IntegrationsFilter } from '../pages/integrations/models/integrations-filter.model';
import { PricingPoliciesFilter } from '../pages/pricing-policies/models/pricing-policies-filter';
import { PricingPolicyRulesFilter } from '../pages/pricing-policy-rules/models/pricing-policy-rules-filter.model';
import { PromisedPaymentsFilter } from '../pages/promised-payments/models/promised-payments-filter.model';
import { ReportUsersFilter } from '../pages/report-users/models/report-users-filter.model';
import { SvbBatchListFilters } from '../pages/svb-ach/models/svb-batch-list-filters.model';
import { SvbV2ListFiltersDTO } from '../pages/svb-v2-ach/models/svb-v2-list-filters.dto';
import { TransactionsFilter } from '../pages/transactions/models/transactions-filter.model';
import { UserStatusEnum } from '../pages/users/models/user-status.enum';
import { UsersFilter } from '../pages/users/models/users-filter.model';

export const USERS_FILTER = 'USERS_FILTER_v6';
export const DEALS_FILTER = 'DEALS_FILTER_v8';
export const DEALS_90_DAYS_ELIGIBLE_FILTER = 'DEALS_90_DAYS_ELIGIBLE_FILTER_V1';
export const FACILITIES_FILTER = 'FACILITIES_FILTER_v9';
export const FACILITY_DEALS_FILTER = 'FACILITY_DEALS_FILTER';
export const PAYMENTS_OVERVIEW_FILTER = 'PAYMENTS_OVERVIEW_FILTER_v2';
export const CREDIT_CARD_FILTER = 'CREDIT_CARD_FILTER_v3';
export const TRANSACTION_FILTER = 'TRANSACTION_FILTER';
export const TRANSACTION_STATUS_REQUESTED_FILTER =
  'TRANSACTION_STATUS_REQUESTED_FILTER_v2';
export const BROKERS_FILTER = 'BROKERS_FILTER_v4';
export const REPORT_USERS_FILTER = 'REPORT_USERS_FILTER_v3';
export const BROKER_USERS_FILTER = 'BROKER_USERS_FILTER_v2';
export const PRICING_POLICIES_FILTER = 'PRICING_POLICIES_FILTER_v3';
export const PROMISED_PAYMENTS_FILTER = 'PROMISED_PAYMENTS_FILTER_3';
export const COMMISSION_ISSUERS_FILTER = 'COMMISSION_ISSUERS_FILTER_2';
export const CDA_FILTER = 'CDA_FILTER';
export const DISCOUNTS_FILTER = 'DISCOUNTS_FILTER_2';
export const INTEGRATIONS_FILTER = 'INTEGRATIONS_FILTER';
export const DEAL_PROMO_CODE_FILTER = 'DEAL_PROMO_CODE';
export const EXTERNAL_DEAL_COMMISSIONS_FILTER =
  'EXTERNAL_DEAL_COMMISSIONS_FILTER_v4';
export const PRICING_POLICY_RULES_FILTER = 'PRICING_POLICY_RULES_FILTER';
export const TRANSACTIONS_DEFAULT_CANDIDATES_FILTER =
  'TRANSACTIONS_DEFAULT_CANDIDATES_FILTER_v2';
export const SVB_LIST_FILTER = 'SVB_LIST_FILTER';
export const SVB_BATCH_LIST_FILTER = 'SVB_BATCH_LIST_FILTER';
export const SVB_V2_LIST_FILTER = 'SVB_V2_LIST_FILTER';

const objectsMap = {
  [USERS_FILTER]: new UsersFilter(),
  [DEALS_FILTER]: new DealsFilter([DealStatusEnum.Requested], 'id', 'desc', [
    UserStatusEnum.Active,
    UserStatusEnum.Blocked,
  ]),
  [DEALS_90_DAYS_ELIGIBLE_FILTER]: new DealsFilter(null, 'id', 'desc'),
  [FACILITY_DEALS_FILTER]: new DealsFilter(
    [DealStatusEnum.Requested],
    'id',
    'desc',
    [UserStatusEnum.Active, UserStatusEnum.Blocked],
  ),
  [FACILITIES_FILTER]: new FacilitiesFilter(),
  [PAYMENTS_OVERVIEW_FILTER]: new DealsFilter([], 'paymentDate', 'desc'),
  [CREDIT_CARD_FILTER]: new CreditCardsFilter('credit_card.id', 'desc'),
  [TRANSACTION_FILTER]: new TransactionsFilter('transaction.id', 'desc'),
  [TRANSACTION_STATUS_REQUESTED_FILTER]: new TransactionsFilter(),
  [TRANSACTIONS_DEFAULT_CANDIDATES_FILTER]: new TransactionsFilter(),
  [BROKERS_FILTER]: new BrokersFilter('id', 'desc'),
  [REPORT_USERS_FILTER]: new ReportUsersFilter(),
  [BROKER_USERS_FILTER]: new BrokerUsersFilter('id', 'desc'),
  [PRICING_POLICIES_FILTER]: new PricingPoliciesFilter(),
  [PROMISED_PAYMENTS_FILTER]: new PromisedPaymentsFilter(),
  [COMMISSION_ISSUERS_FILTER]: new CommissionIssuersFilter('id', 'desc'),
  [CDA_FILTER]: new CDAFilter(),
  [DISCOUNTS_FILTER]: new DiscountsFilter(),
  [INTEGRATIONS_FILTER]: new IntegrationsFilter(),
  [DEAL_PROMO_CODE_FILTER]: new DealPromoCodeFilter(),
  [EXTERNAL_DEAL_COMMISSIONS_FILTER]: new ExternalDealCommissionFilter(
    'draftToReadyAt',
    'desc',
  ),
  [PRICING_POLICY_RULES_FILTER]: new PricingPolicyRulesFilter(),
  [SVB_LIST_FILTER]: new SvbListFilter(),
  [SVB_BATCH_LIST_FILTER]: new SvbBatchListFilters(),
  [SVB_V2_LIST_FILTER]: new SvbV2ListFiltersDTO(),
};

type objectsMapType = typeof objectsMap;

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  constructor() {}

  public get<K extends keyof objectsMapType>(key: K): objectsMapType[K] {
    const valueStr = localStorage.getItem(key);
    if (!valueStr) {
      return { ...objectsMap[key] };
    }

    try {
      return JSON.parse(valueStr);
    } catch (e) {
      return { ...objectsMap[key] };
    }
  }

  public set<K extends keyof objectsMapType>(key: K, value: objectsMapType[K]) {
    localStorage.setItem(key, JSON.stringify(value));
  }

  public getBoolean(key: string): boolean {
    return localStorage.getItem(key) === 'true';
  }

  public setBoolean(key: string, value: boolean) {
    localStorage.setItem(key, `${value}`);
  }

  public getAnyObject<T>(key: string): T | undefined {
    const valueStr = localStorage.getItem(key);
    if (!valueStr) {
      return undefined;
    }

    try {
      return JSON.parse(valueStr);
    } catch (e) {
      return undefined;
    }
  }

  public setAnyObject<T>(key: string, value: T): void {
    localStorage.setItem(key, JSON.stringify(value));
  }
}
